import {
  Accordion,
  AspectRatio,
  Blockquote,
  Box,
  Code,
  Stack,
  Text
} from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { ListItemDrawer } from '@/components/ui-shared/ListItemDrawer/ListItemDrawer'
import { useGetVlmResult } from '@/queries/vlmQueries'
import { VlmResult } from '@/types/vlm'
import { VlmResultDetailsTable } from './VlmResultDetailsTable'
import { VlmResultObjectsTable } from './VlmResultObjectsTable'

type VlmResultDrawerProps = {
  result: VlmResult | null
  onClose: () => void
}

export const VlmResultDrawer = ({ result, onClose }: VlmResultDrawerProps) => {
  const intl = useIntl()
  // The list row renders the text; the detail fetch only adds the media URLs, so a
  // failed or pending fetch degrades to "no video" rather than an error state.
  const { data: detail } = useGetVlmResult(result?.id)
  const videoUrl = detail?.video_url
  const userPrompt = detail?.user_prompt ?? result?.user_prompt

  return (
    <ListItemDrawer
      opened={!!result}
      title={<FormattedMessage id="vlm.results.drawerTitle" />}
      onClose={onClose}
    >
      {result && (
        <Stack gap="xl" maw={1100}>
          {videoUrl && (
            <AspectRatio ratio={16 / 9}>
              <Box>
                <video
                  width="100%"
                  style={{ borderRadius: 8 }}
                  src={videoUrl}
                  poster={detail?.thumbnail_url ?? undefined}
                  aria-label={intl.formatMessage({
                    id: 'vlm.results.videoLabel'
                  })}
                  controls
                />
              </Box>
            </AspectRatio>
          )}

          {userPrompt && (
            <Stack gap="xs">
              <Text fw={600}>
                <FormattedMessage id="vlm.results.fields.userPrompt" />
              </Text>
              <Blockquote p="sm" fz="sm">
                {userPrompt}
              </Blockquote>
            </Stack>
          )}

          <VlmResultDetailsTable result={result} />

          <VlmResultObjectsTable objects={result.result.objects} />

          <Accordion variant="contained">
            <Accordion.Item value="json">
              <Accordion.Control>
                <FormattedMessage id="vlm.results.rawJson" />
              </Accordion.Control>
              <Accordion.Panel>
                <Code block>{JSON.stringify(result.result, null, 2)}</Code>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      )}
    </ListItemDrawer>
  )
}
